import React, { useState } from 'react';
import { ListItemText, Menu, MenuItem } from '@mui/material';
import AssignmentTurnedInOutlinedIcon from '@mui/icons-material/AssignmentTurnedInOutlined';
import EventAvailableOutlinedIcon from '@mui/icons-material/EventAvailableOutlined';
import AttachFileOutlinedIcon from '@mui/icons-material/AttachFileOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import PowerSettingsNewOutlinedIcon from '@mui/icons-material/PowerSettingsNewOutlined';
import RestoreOutlinedIcon from '@mui/icons-material/RestoreOutlined';
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';
import LinkOutlinedIcon from '@mui/icons-material/LinkOutlined';
import { RowAction } from './DocumentsBlock';
import {
  attachmentsLabel, authorizationStatusShortLabel, referenceLabel, usageLabel, validityInfo,
} from './documentFormat';
import { AUTHORIZATION_STATUS_OPTIONS, authorizationTypeLabel } from '../../../../appointments/v2/utils/billingType';
import styles from './InsuranceDocumentsSection.module.css';

const STATUS_CLASS = {
  pending: styles.statusPending,
  authorized: styles.statusAuthorized,
  denied: styles.statusDenied,
  cancelled: styles.statusCancelled,
};

/**
 * Chip de situação que abre um menu para trocar direto na linha. O menu usa
 * os rótulos completos ("Autorizada (informada pela recepção)") para deixar
 * claro que é um registro manual.
 */
function StatusChip({ status, disabled, onChange }) {
  const [anchor, setAnchor] = useState(null);
  const open = Boolean(anchor);

  const pick = (next) => {
    setAnchor(null);
    if (next !== status) onChange(next);
  };

  return (
    <>
      <button
        type="button"
        className={`${styles.statusChip} ${STATUS_CLASS[status] || styles.statusPending}`}
        onClick={(event) => setAnchor(event.currentTarget)}
        disabled={disabled}
        aria-haspopup="menu"
        aria-expanded={open ? 'true' : undefined}
        title="Mudar a situação"
      >
        <span className={styles.statusDot} />
        {authorizationStatusShortLabel(status)}
        <ExpandMoreRoundedIcon />
      </button>
      <Menu anchorEl={anchor} open={open} onClose={() => setAnchor(null)}>
        {AUTHORIZATION_STATUS_OPTIONS.map((option) => (
          <MenuItem key={option.id} selected={option.id === status} onClick={() => pick(option.id)} dense>
            <ListItemText primary={option.label} />
          </MenuItem>
        ))}
      </Menu>
    </>
  );
}

/**
 * Uma guia: número (ou "Sem número"), operadora e tipo, pedido ligado,
 * situação (chip clicável), validade com "Vencida", anexos e uso.
 */
function AuthorizationRow({ authorization, busy, onEdit, onToggle, onStatusChange, onAttachments }) {
  const inactive = authorization.active === 0 || authorization.active === false;
  const validity = validityInfo(authorization.valid_until, authorization.expired);
  const title = referenceLabel(authorization);
  // O rótulo da carteirinha já começa pela operadora: não repetir
  const secondary = [
    authorization.insurance_label || authorization.provider_name || 'Operadora não informada',
    authorizationTypeLabel(authorization.authorization_type),
  ].filter(Boolean).join(' · ');
  const usage = authorization.authorized_quantity
    ? `${Number(authorization.used_count) || 0} de ${authorization.authorized_quantity} usadas`
    : usageLabel(authorization.used_count, true);

  return (
    <li className={`${styles.row} ${inactive ? styles.muted : ''}`}>
      <span className={`${styles.rowIcon} ${inactive ? styles.rowIconMuted : ''}`}>
        <AssignmentTurnedInOutlinedIcon fontSize="small" />
      </span>

      <div className={styles.identity}>
        <span className={styles.tags}>
          <span className={`${styles.name} ${styles.ellipsis}`} title={title}>{title}</span>
          {inactive
            ? <span className={`${styles.tag} ${styles.tagInactive}`}>Inativa</span>
            : <StatusChip status={authorization.status} disabled={busy} onChange={(next) => onStatusChange(authorization, next)} />}
          {!inactive && validity.expired && <span className={`${styles.tag} ${styles.tagExpired}`}>Vencida</span>}
        </span>
        <span className={`${styles.secondary} ${styles.ellipsis}`} title={secondary}>{secondary}</span>
        {authorization.medical_request_title && (
          <span className={`${styles.tag} ${styles.tagLinked}`} title="Pedido médico ligado a esta guia">
            <LinkOutlinedIcon sx={{ fontSize: 14 }} />
            <span className={styles.ellipsis}>{authorization.medical_request_title}</span>
          </span>
        )}
      </div>

      <div className={styles.side}>
        <span
          className={`${styles.detail} ${validity.tone === 'muted' ? styles.detailMuted : ''} ${validity.expired ? styles.detailExpired : ''}`}
        >
          <EventAvailableOutlinedIcon className={styles.detailIcon} />
          {validity.label}
        </span>
        <span className={styles.counters}>
          <button type="button" className={styles.counterLink} onClick={() => onAttachments(authorization)}>
            <AttachFileOutlinedIcon />
            {attachmentsLabel(authorization.attachments_count)}
          </button>
          <span>{usage}</span>
        </span>
      </div>

      <span className={styles.actions}>
        <RowAction title="Anexos" label={`Anexos da ${title}`} disabled={busy} onClick={() => onAttachments(authorization)} Icon={AttachFileOutlinedIcon} />
        <RowAction title="Editar guia" label={`Editar ${title}`} disabled={busy} onClick={() => onEdit(authorization)} Icon={EditOutlinedIcon} />
        {inactive ? (
          <RowAction title="Reativar guia" label={`Reativar ${title}`} disabled={busy} onClick={() => onToggle(authorization)} Icon={RestoreOutlinedIcon} tone="positive" />
        ) : (
          <RowAction title="Inativar guia" label={`Inativar ${title}`} disabled={busy} onClick={() => onToggle(authorization)} Icon={PowerSettingsNewOutlinedIcon} tone="warning" />
        )}
      </span>
    </li>
  );
}

export default AuthorizationRow;
